import React from 'react';
import { Autocomplete, Button, GridContainer, GridItem, Modal, TextField } from '../../../components/shared';
import { Box, Grid } from '@mui/material';
import { MESSAGE } from '../../../constants/message';
import { useGetGlobalAccountNumberQuery } from '../../../services/AccountNumber/AccountNumberService';
import { useDebounce } from '../../../hooks';
import { getX3AccountValue, formatAccountNumberForAutocomplete } from './userDetailsUtils';

const EditUserGLNumberModal = (props) => {
  const { onClose = () => {}, onConfirm = () => {}, selectedSublocation, employeeDetails = {} } = props;
  const [showGLModal, setShowGLModal] = React.useState(false);
  const [sublocations, setSublocations] = React.useState([]);
  const { firstName, lastName } = employeeDetails || {};
  const [GLNumberSearchText, setGLNumberSearchText] = React.useState('');
  const debouncedGLNumberSearchText = useDebounce(GLNumberSearchText, 300);

  const existingAccountNumbers = React.useMemo(() => {
    if (!selectedSublocation || selectedSublocation.length === 0) {
      return [];
    }

    return selectedSublocation
      .map((subloc) => {
        const accountNum = subloc?.accountNumber;
        if (accountNum?.X3_accountNumber) {
          return null;
        }
        return accountNum?.accountNumber || accountNum?.name || accountNum;
      })
      .filter(Boolean);
  }, [selectedSublocation]);

  const { currentData, isSuccess } = useGetGlobalAccountNumberQuery({
    page: 1,
    perPage: 100,
    searchParams: `X3_accountNumber=${debouncedGLNumberSearchText}`,
  });

  const { currentData: existingData, isSuccess: existingSuccess } = useGetGlobalAccountNumberQuery(
    {
      page: 1,
      perPage: 100,
      searchParams: existingAccountNumbers.length > 0 ? `accountNumber=${existingAccountNumbers[0]}` : '',
    },
    {
      skip: existingAccountNumbers.length === 0,
    },
  );

  const accountNumbers = React.useMemo(() => {
    const mainAccounts = isSuccess
      ? currentData?.data?.globalAccountNumber
          ?.filter((value) => value?.X3_accountNumber?.trim())
          ?.map((value) => formatAccountNumberForAutocomplete(value))
          ?.filter(Boolean) ?? []
      : [];

    const existingAccounts = existingSuccess
      ? existingData?.data?.globalAccountNumber
          ?.filter((value) => value?.X3_accountNumber?.trim())
          ?.map((value) => formatAccountNumberForAutocomplete(value))
          ?.filter(Boolean) ?? []
      : [];

    const allAccounts = [...existingAccounts, ...mainAccounts];
    const uniqueAccounts = allAccounts.filter(
      (account, index, self) => index === self.findIndex((a) => a.X3_accountNumber === account.X3_accountNumber),
    );

    return uniqueAccounts;
  }, [currentData, isSuccess, existingData, existingSuccess]);

  React.useEffect(() => {
    if (selectedSublocation && selectedSublocation.length > 0) {
      setShowGLModal(true);
      setSublocations(selectedSublocation);
    }
  }, [selectedSublocation]);

  const handleClose = () => {
    onClose();
    setShowGLModal(false);
  };

  const handleConfirm = () => {
    onConfirm(sublocations);
    setShowGLModal(false);
  };

  const handleGLNumberChange = (e) => {
    const { name, value } = e;
    const [field, index] = name.split('_');
    const tempSublocations = [...sublocations];
    tempSublocations[index][field] = value;
    setSublocations(tempSublocations);
    setGLNumberSearchText('');
  };

  // TODO: Add compare logic for prev vs new
  const disableConfirm = false;

  return (
    <Modal
      open={showGLModal}
      onClose={handleClose}
      title={`Edit GL for ${firstName} ${lastName}`}
      titleVariant="subtitle10"
      actionButtons={
        <Box my={3}>
          <Button variant="contained" size="large" onClick={handleConfirm} disabled={disableConfirm}>
            {MESSAGE.CONFIRM}
          </Button>
          <Button size="large" onClick={handleClose} variant={'text'}>
            {MESSAGE.CANCEL}
          </Button>
        </Box>
      }
      maxWidth={'sm'}
    >
      {sublocations?.map((sublocation, index) => {
        let { id, name, accountNumber } = sublocation;

        let matchedOption = null;
        if (accountNumber) {
          const x3Value = getX3AccountValue(accountNumber);
          const glValue = accountNumber?.accountNumber || accountNumber?.name || accountNumber;

          if (accountNumbers && accountNumbers.length > 0) {
            if (x3Value) {
              matchedOption =
                accountNumbers.find(
                  (option) =>
                    option?.X3_accountNumber?.trim() === x3Value || option?.value === x3Value || option?.id === x3Value,
                ) || null;
            }

            if (!matchedOption && glValue) {
              matchedOption = accountNumbers.find((option) => option?.accountNumber === glValue) || null;
            }
          }

          if (!matchedOption && x3Value) {
            matchedOption = formatAccountNumberForAutocomplete(accountNumber);
          }
        }

        return (
          <Grid key={id}>
            <div>{name}:</div>
            <GridContainer rowSpacing={2} gap={2}>
              <GridItem xs={3}>
                <Autocomplete
                  label="X3 Number"
                  name={`accountNumber_${index}`}
                  value={matchedOption || null}
                  options={accountNumbers || []}
                  getOptionLabel={(option) => option.text || option.name || ''}
                  onChange={(_, value) => handleGLNumberChange({ name: `accountNumber_${index}`, value })}
                  onInputChange={(_, value) => setGLNumberSearchText(value)}
                />
              </GridItem>
              <GridItem xs={3}>
                <TextField
                  label="GL Name"
                  value={matchedOption?.accountName || accountNumber?.accountName || ''}
                  disabled
                />
              </GridItem>
              <GridItem xs={3}>
                <TextField
                  label="GL Number"
                  value={matchedOption?.accountNumber || accountNumber?.accountNumber || ''}
                  disabled
                />
              </GridItem>
            </GridContainer>
          </Grid>
        );
      })}
    </Modal>
  );
};

export default EditUserGLNumberModal;
